import { enqueueSnackbar } from 'notistack';
import { includes } from 'ramda';
import { createAction } from 'redux-actions';

import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelists } from 'constants/codelists';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { INFO_ALREADY_PROCESSED_ERR_MSG } from 'features/other/processInformation/constants';
import typeTasks from 'features/other/typeTasks';
import taskDetail from 'features/views/taskDetail';
import taskSearch from 'features/views/taskSearch';
import { getCodelistByName, getCodelistPropById, history } from 'utils';

import { isOnTaskDetail } from '../taskDetail/utils';
import taskOverview from '../taskOverview';
import { FEED_BACK_CLOSE, FEED_BACK_OPEN } from './actionTypes';
import { ALREADY_RESOLVED, HAS_NO_ACCESS_ERR_MSG } from './constants';
import { TASK_FEEDBACK_FORM_NAME } from './form';

export const openFeedback = createAction(FEED_BACK_OPEN);

export const closeFeedback = createAction(FEED_BACK_CLOSE);

// used in taskFeedback and infoProcessing on taskDetail
export const postFeedback =
	(taskId, taskTypeId, finishTypeId) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const taskTypesCodelist = getCodelistByName(codelists.taskTypes)(getState());
		const isInfo = getCodelistPropById(taskTypeId, taskTypesCodelist, 'informative');

		let formData;
		if (isInfo) {
			// postFeedback is also used when "processing information"
			// but when task is "information" - feedback component with Form is not rendered
			formData = EMPTY_VALUES.OBJECT;
		} else {
			formData = await getFormData(TASK_FEEDBACK_FORM_NAME);
		}

		return dispatch(
			fetch.actions.post(fetch.constants.urls.taskFinish, {
				uniqueReqID: taskId,
				replaceParams: { id: taskId },
				body: {
					finishTypeId,
					...(formData?.policyNumber && {
						feedbackExtra: {
							feedback: {
								policyNo: formData?.policyNumber,
							},
						},
					}),
				},
				onSuccess: [
					() => () => {
						isInfo &&
							enqueueSnackbar(getLabelMessage('toastr:approveInformationProccessed'), {
								variant: 'success',
							});

						if (!isInfo) {
							enqueueSnackbar(getLabelMessage('toastr:approveFeedbackHeader'), { variant: 'success' });
							formData?.policyNumber && sendControlNext(TASK_FEEDBACK_FORM_NAME, resetForm());
							dispatch(closeFeedback(taskTypeId));
						}

						dispatch(taskDetail.actions.fetchDetails(taskId));
						dispatch(typeTasks.actions.reloadCurrentPage(taskTypeId));
						dispatch(taskOverview.actions.reload());
						dispatch(taskSearch.actions.paginationClick(0));
					},
				],
				onError: [
					({ failure }) =>
						() => {
							if (failure?.status === 403) {
								// taskFeedback update (detail / tasksOverview)
								if (includes(ALREADY_RESOLVED, failure?.parsedContent?.message)) {
									enqueueSnackbar(getLabelMessage('toastr:infoOrTaskAlreadyResolved'), {
										variant: 'error',
									});

									if (isOnTaskDetail()) {
										!isInfo && dispatch(closeFeedback(taskTypeId));
										dispatch(taskDetail.actions.fetchDetails(taskId));
									}

									if (window.location.pathname === '/tasks') {
										!isInfo && dispatch(closeFeedback(taskTypeId));
										dispatch(typeTasks.actions.reloadCurrentPage(taskTypeId));
										dispatch(taskOverview.actions.reload());
									}
								}

								// taskFeedback update (detail / tasksOverview)
								if (includes(HAS_NO_ACCESS_ERR_MSG, failure?.parsedContent?.message)) {
									enqueueSnackbar(getLabelMessage('toastr:cannotProcessInfoTaskUserIsNotResolver'), {
										variant: 'error',
									});

									if (isOnTaskDetail()) {
										history.go(-1);
									}

									if (window.location.pathname === '/tasks') {
										!isInfo && dispatch(closeFeedback(taskTypeId));
										dispatch(typeTasks.actions.reloadCurrentPage(taskTypeId));
										dispatch(taskOverview.actions.reload());
									}
								}

								// info processing on detail
								if (failure?.parsedContent?.message === INFO_ALREADY_PROCESSED_ERR_MSG) {
									enqueueSnackbar(getLabelMessage('toastr:infoAlreadyProcessed'), {
										variant: 'error',
									});
									dispatch(taskDetail.actions.fetchDetails(taskId));
								}
							}
						},
				],
				shouldReportError: ({ failure }) => {
					return failure.status !== 403;
				},
			}),
		);
	};

export const resetTaskFeedback =
	(taskTypeId) =>
	(dispatch, getState, { sendControlNext }) => {
		sendControlNext(resetForm(TASK_FEEDBACK_FORM_NAME));
		dispatch(closeFeedback(taskTypeId));
	};

export default { closeFeedback, openFeedback, postFeedback };
